'use client';

import { useMemoizedFn } from 'ahooks';
import { useEffect, useRef } from 'react';
import { isClawProvisioning, useClaw } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import { getLoginUrl } from '@/app/utils';
import { usePathname, useRouter } from '@/i18n/routing';

const derivePageFlags = (pathname: string) => {
  const isShareSessionPage = pathname.includes('/claw/share-session');
  const isFilePage = pathname.includes('/claw/file');
  const isCasesPage = pathname.includes('/claw/cases');
  // next-intl 的 usePathname 已剥离 locale 前缀，根路径形如 "/claw" 或 "/claw/"
  const isClawRootPage = /^\/claw\/?$/.test(pathname);
  // 公开页面：未登录用户也允许停留（分享会话、文件预览）
  const allowsAnonymous = isShareSessionPage || isFilePage;
  // 不强制 clawid 的页面：公开页面 + 根 /claw（Welcome）+ 案例展示
  const allowsWithoutClaw = allowsAnonymous || isCasesPage || isClawRootPage;
  return {
    isShareSessionPage,
    allowsAnonymous,
    // 需要 clawid 才能停留的页面：例如 /claw/[sessionsId]
    requiresClaw: !allowsWithoutClaw,
  };
};

/**
 * Claw 进入页面时的统一准入流程：
 * - 公开页面（/claw/share-session、/claw/file）：未登录或无 claw 时都允许停留；
 *   登录用户照常调用 GetMyClaws 同步 store
 * - 需要登录的页面（/claw 根、/claw/cases、/claw/[sessionsId] 等）：
 *   - 未登录：直接 router.replace('/login?redirect=<原 URL>')，登录成功后回到原页面
 * - 需要 clawid 的页面（/claw/[sessionsId] 等）：
 *   - 已登录但首个 claw 不是 ACTIVE（含无 claw、provisioning）→ 回跳 /claw 让 Welcome 接管
 * - 进入页面后若 store 中的 clawStatus 因外部触发（Welcome 中的开通按钮）
 *   切换到 provisioning，则启动全局轮询作为兜底，防止用户中途关闭页面后开通状态丢失
 * - share-session 页首次进入且 claw 未就绪时，自动折叠侧边栏一次
 */
export const useInitClaw = () => {
  const router = useRouter();
  const pathname = usePathname();
  const storeApi = useStoreApi();
  const { userInfo, isUserInfoInitialized, clawStatus, toggleClawSidebar } = useStore((state) => ({
    userInfo: state.userInfo,
    isUserInfoInitialized: state.isUserInfoInitialized,
    clawStatus: state.clawStatus,
    toggleClawSidebar: state.toggleClawSidebar,
  }));

  const { isShareSessionPage, allowsAnonymous, requiresClaw } = derivePageFlags(pathname);
  const { getMyClawsAsync, startClawStatusPolling } = useClaw();

  // share-session 页面离开后重置 once 标志，下次再进入时允许再次触发一次折叠
  const hasAutoCollapsedForShareRef = useRef(false);
  useEffect(() => {
    if (!isShareSessionPage) hasAutoCollapsedForShareRef.current = false;
  }, [isShareSessionPage]);

  const collapseSidebarForShareOnce = useMemoizedFn(() => {
    if (!isShareSessionPage || hasAutoCollapsedForShareRef.current) return;
    hasAutoCollapsedForShareRef.current = true;
    if (!storeApi.getState().clawSidebarCollapsed) {
      toggleClawSidebar();
    }
  });

  const initClaw = useMemoizedFn(async () => {
    const res = await getMyClawsAsync();
    if (!res?.data) {
      collapseSidebarForShareOnce();
      return;
    }

    const first = res.data.claws?.[0];
    const firstStatus = first?.status ?? null;

    if (first && !isClawProvisioning(firstStatus)) {
      return;
    }

    // claw 未就绪（无 claw 或仍在 provisioning）：需要 clawid 的页面统一回跳 /claw，
    // 让 Welcome 接管开通入口与轮询兜底，避免业务页空挂等待。
    if (requiresClaw) {
      router.replace('/claw');
      return;
    }

    collapseSidebarForShareOnce();
  });

  useEffect(() => {
    if (!isUserInfoInitialized) return;

    if (userInfo) {
      initClaw();
      return;
    }

    // 公开页面（share-session / file）：未登录也允许停留
    if (allowsAnonymous) {
      collapseSidebarForShareOnce();
    }
  }, [isUserInfoInitialized, userInfo]);

  // 未登录用户在 claw layout 内做客户端路由切换（例如 share-session → /claw 的 Try Now）时，
  // userInfo 没变化主 effect 不会重跑，需要独立 effect 跟随 pathname 兜底跳转登录页。
  useEffect(() => {
    if (!isUserInfoInitialized) return;
    if (userInfo) return;
    if (allowsAnonymous) return;
    router.replace(getLoginUrl(`${pathname}${window.location.search}`));
  }, [isUserInfoInitialized, userInfo, allowsAnonymous, pathname]);

  // 兜底：若 init 流程结束后，store 中的 clawStatus 因外部入口（Welcome 中的开通按钮）
  // 切换到 provisioning，则补启全局轮询，确保用户即使中途离开也能等到就绪。
  // 注意：不能用 activeClaw 作为跳过条件 —— 从 EXPIRED 重新开通时 activeClaw 仍然 truthy（旧实例残留），
  // 只有 clawStatus 本身能区分 provisioning 与已就绪。
  useEffect(() => {
    if (!isClawProvisioning(clawStatus)) return;
    startClawStatusPolling();
  }, [clawStatus]);
};
